import React, { useContext, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
} from "@mui/material";
import { CircularProgress } from "@material-ui/core";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import { useSnackbar } from "notistack";

import { viewportContext } from "../../../context/ViewportContext";
import { updateRecordDining } from "../../../service/endpoint-manager";
import { diningAuditPDFReport } from "./DiningAuditPDFReport";

import styles from "../../../styles.module.css";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";

const LoadingSpinner = () => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        // height: "30px",
        // width: "30vh",
      }}
    >
      <CircularProgress size={25} />
    </div>
  );
};

const DiningAuditSummary = ({
  diningRecords,
  setDiningRecords,
  filteredDiningRecords,
  setFilteredDiningRecords,
  userNames,
  dataFetched,
  isDashboard,
  setIsDashboard,
}) => {
  const { screenType } = useContext(viewportContext);
  const { enqueueSnackbar } = useSnackbar();

  const [deletingRecordId, setDeletingRecordId] = useState(null);

  function findUserName(userNames, id) {
    const user = userNames.find((user) => user.user_id === id);
    return user ? user.userName : null;
  }

  // delete records from the list - uses the update api
  const handleDelete = async (recordId) => {
    setDeletingRecordId(recordId);
    try {
      const response = await updateRecordDining(recordId, {
        deleted: true,
      });
      if (response.status === 200) {
        const updatedRecords = diningRecords.filter(
          (record) => record.id !== recordId
        );
        setDiningRecords(updatedRecords);
        setDeletingRecordId(null);
        enqueueSnackbar("Record Deleted Successfully", {
          variant: "success",
        });
      } else {
        console.error("Failed to delete the record", response.data);
        setDeletingRecordId(null);
        enqueueSnackbar("Failed to delete record", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(
        "Error deleting the record:",
        error.response ? error.response.data : error
      );
      setDeletingRecordId(null);
    }
  };

  // to restore deleted records - uses update api
  const handleRestore = async (recordId) => {
    setDeletingRecordId(recordId);
    try {
      const response = await updateRecordDining(recordId, {
        deleted: false,
      });
      if (response.status === 200) {
        const updatedRecords = diningRecords.filter(
          (record) => record.id !== recordId
        );
        setDiningRecords(updatedRecords);
        setDeletingRecordId(null);
        enqueueSnackbar("Record Restored Successfully", {
          variant: "success",
        });
      } else {
        console.error("Failed to restore the record", response.data);
        setDeletingRecordId(null);
        enqueueSnackbar("Failed to restore record", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(
        "Error restoring the record:",
        error.response ? error.response.data : error
      );
      setDeletingRecordId(null);
    }
  };

  return (
    <Paper className={styles.paperStyles}>
      <div>
        <div className={styles.titleButtonRow}>
          <Typography variant="h5">Audit Report Summary</Typography>
          <div className={styles.exportButtonContainer}>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={clsx(
                  styles.primaryButton,
                  styles.complianceTableButtons
                )}
                onClick={() => setIsDashboard(!isDashboard)}
              >
                + New Observation
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={clsx(
                  styles.secondaryButton,
                  styles.complianceTableButtons
                )}
                onClick={() =>
                  diningAuditPDFReport(filteredDiningRecords, userNames)
                }
              >
                {screenType.isMobile ? "PDF" : "Export PDF"}
              </Button>
            </div>
          </div>
        </div>
        <TableContainer className={styles.narcoticsReportTableContainer}>
          {dataFetched ? (
            <Table stickyHeader aria-label="dining records">
              <TableHead>
                <TableRow className={styles.narcoticsReportHeader}>
                  <TableCell>Audited By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Audit Completed For</TableCell>
                  <TableCell>Meal Observed</TableCell>
                  <TableCell>Snack Audit</TableCell>
                  <TableCell>Environment</TableCell>
                  <TableCell>Setup</TableCell>
                  <TableCell>Assistance</TableCell>
                  <TableCell>Activities/Staffing</TableCell>
                  <TableCell>Interruption/Pace</TableCell>

                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDiningRecords.map((record, i) => (
                  <TableRow>
                    <TableCell>
                      {findUserName(userNames, record.auditedBy)}
                    </TableCell>
                    <TableCell>
                      {new Date(record.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{record.facility}</TableCell>
                    <TableCell>{record.unit}</TableCell>
                    <TableCell>{record.auditCompletedFor}</TableCell>
                    <TableCell>{record.mealObserved}</TableCell>
                    <TableCell>
                      {record.snackAudit.compliance.percentage >= 0.6
                        ? `Compliant (${(
                            record.snackAudit.compliance.percentage * 100
                          ).toFixed(2)}%)`
                        : `Non-Compliant (${(
                            record.snackAudit.compliance.percentage * 100
                          ).toFixed(2)}%)`}
                    </TableCell>
                    <TableCell>
                      {record.diningAudit.environment.compliance.percentage >=
                      0.6
                        ? `Compliant (${(
                            record.diningAudit.environment.compliance
                              .percentage * 100
                          ).toFixed(2)}%)`
                        : `Non-Compliant (${(
                            record.diningAudit.environment.compliance
                              .percentage * 100
                          ).toFixed(2)}%)`}
                    </TableCell>
                    <TableCell>
                      {record.diningAudit.setup.compliance.percentage >= 0.6
                        ? `Compliant (${(
                            record.diningAudit.setup.compliance.percentage * 100
                          ).toFixed(2)}%)`
                        : `Non-Compliant (${(
                            record.diningAudit.setup.compliance.percentage * 100
                          ).toFixed(2)}%)`}
                    </TableCell>
                    <TableCell>
                      {record.diningAudit.assistance.compliance.percentage >=
                      0.6
                        ? `Compliant (${(
                            record.diningAudit.assistance.compliance
                              .percentage * 100
                          ).toFixed(2)}%)`
                        : `Non-Compliant (${(
                            record.diningAudit.assistance.compliance
                              .percentage * 100
                          ).toFixed(2)}%)`}
                    </TableCell>
                    <TableCell>
                      {record.diningAudit.activities.compliance.percentage >=
                      0.6
                        ? `Compliant (${(
                            record.diningAudit.activities.compliance
                              .percentage * 100
                          ).toFixed(2)}%)`
                        : `Non-Compliant (${(
                            record.diningAudit.activities.compliance
                              .percentage * 100
                          ).toFixed(2)}%)`}
                    </TableCell>
                    <TableCell>
                      {record.diningAudit.interruption.compliance.percentage >=
                      0.6
                        ? `Compliant (${(
                            record.diningAudit.interruption.compliance
                              .percentage * 100
                          ).toFixed(2)}%)`
                        : `Non-Compliant (${(
                            record.diningAudit.interruption.compliance
                              .percentage * 100
                          ).toFixed(2)}%)`}
                    </TableCell>
                    <TableCell>
                      {deletingRecordId === record.id ? (
                        <LoadingSpinner />
                      ) : record.deleted ? (
                        <IconButton
                          onClick={() => handleRestore(record.id)}
                          aria-label="restore"
                        >
                          <ReplayIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => handleDelete(record.id)}
                          aria-label="delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredDiningRecords.length === 0 && (
                  <p
                    style={{
                      textAlign: "center",
                      marginTop: "30px",
                      width: "100%",
                    }}
                  >
                    No Records Found
                  </p>
                )}
              </TableBody>
            </Table>
          ) : (
            <p style={{ textAlign: "center", marginTop: "30px" }}>
              Please Wait While The Data is Being Fetched ...
            </p>
          )}
        </TableContainer>
      </div>
    </Paper>
  );
};

export default DiningAuditSummary;
